import { SIDE_TABLE_COLUMN_TYPES, tableSideColumnMapper } from "@loanbossinc/component-library";
import { defaultField, mapRefSubtext } from "helpers/row-logic-helper";

import {
  shouldShowOriginationFeeAmount,
  shouldShowModificationFee,
  shouldShowBrokerFeeAmount,
  shouldShowLenderLegalFeeAmount,
  shouldShowBorrowerLegalFeeAmount,
  shouldShowInspectionFeeAmount,
  shouldShowAppraisalFeeAmount,
  shouldShowRateLockFeeAmount,
  shouldShowHedgeFeeAmount,
  shouldShowTitleAmount,
  shouldShowOtherAmount,
  shouldShowTotalAmount
} from "./section-loan-closing-costs-rows-logic";

/*
 * NOTE: This section needs loanTypeId to decide if it is going to show something
 *       so the path is not just to closingCosts
 */

const DEFAULT_WIDTH = 138;

export const rows = {
  originationFeeAmount: {
    isVisible: data => shouldShowOriginationFeeAmount(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Origination Fee",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
        map: loan => defaultField(loan, "closingCosts.originationFeeAmount"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  modificationFee: {
    isVisible: data => shouldShowModificationFee(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Modification Fee",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
        map: loan => defaultField(loan, "closingCosts.modificationFeeAmount"),
        Cell: tableSideColumnMapper
      }
    ]
  },
  brokerFeeAmount: {
    isVisible: data => shouldShowBrokerFeeAmount(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Broker Fee",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR_SUBTEXT,
        map: loan => defaultField(loan, "closingCosts.brokerFeeAmount"),
        Cell: tableSideColumnMapper,
        subtextMappers: [
          { map: loan => mapRefSubtext(loan, "details.brokerCompany.name") }
        ]
      }
    ]
  },

  lenderLegalFeeAmount: {
    isVisible: data => shouldShowLenderLegalFeeAmount(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Lender Legal",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR_SUBTEXT,
        map: loan => defaultField(loan, "closingCosts.lenderLegalFeeAmount"),
        Cell: tableSideColumnMapper,
        subtextMappers: [
          { map: loan => mapRefSubtext(loan, "details.lenderCounselCompany.name") }
        ]
      }
    ]
  },

  borrowerLegalFeeAmount: {
    isVisible: data => shouldShowBorrowerLegalFeeAmount(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Borrower Legal",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR_SUBTEXT,
        map: loan => defaultField(loan, "closingCosts.borrowerLegalFeeAmount"),
        Cell: tableSideColumnMapper,
        subtextMappers: [
          { map: loan => mapRefSubtext(loan, "details.borrowerCounselCompany.name") }
        ]
      }
    ]
  },

  inspectionFeeAmount: {
    isVisible: data => shouldShowInspectionFeeAmount(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Inspection Fee",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
        map: loan => defaultField(loan, "closingCosts.inspectionFeeAmount"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  appraisalFeeAmount: {
    isVisible: data => shouldShowAppraisalFeeAmount(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Appraisal Fee",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
        map: loan => defaultField(loan, "closingCosts.appraisalFeeAmount"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  rateLockFeeAmount: {
    isVisible: data => shouldShowRateLockFeeAmount(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Rate Lock",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
        map: loan => defaultField(loan, "closingCosts.rateLockFeeAmount"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  hedgeFeeAmount: {
    isVisible: data => shouldShowHedgeFeeAmount(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Hedge Cost",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
        map: loan => defaultField(loan, "closingCosts.hedgeFeeAmount"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  titleAmount: {
    isVisible: data => shouldShowTitleAmount(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Title",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
        map: loan => defaultField(loan, "closingCosts.titleAmount"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  otherAmount: {
    isVisible: data => shouldShowOtherAmount(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Other Fees",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
        map: loan => defaultField(loan, "closingCosts.otherAmount"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  totalAmount: {
    isVisible: data => shouldShowTotalAmount(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Total Closing Costs",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
        map: loan => defaultField(loan, "closingCosts.totalAmount"),
        Cell: tableSideColumnMapper
      }
    ]
  }
};
